import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddBarcodeModal } from './BarcodeModal';
import { PreviewModal } from './PreviewModal';

const JoditEditor = React.lazy(() => import('jodit-react'));

export const EM_TYPE_TEXT = 'EM_TYPE_TEXT';
export const EM_TYPE_BARCODE = 'EM_TYPE_BARCODE';

type VariableType = {
  label: string;
  value: string;
  type?: string;
  barcode_types?: string[];
};

type Props = {
  initialValue: {
    template: string;
    name: string;
    pageSize: string;
    subject: string;
  };
  onSave: (d: {
    template: string;
    name: string;
    pageSize: string;
    subject: string;
  }) => void;
  onCancel: () => void;
  menuItems: {
    label: string;
    value: string;
    variables: VariableType[];
  }[];
  variables: { key: string; value: string; barcode_types?: string[] }[];

  translate?: (key: string, defaultValue: string) => string;
};

const defaultProps = {
  initialValue: {
    template: '',
    name: '',
    pageSize: 'A4',
    subject: 'Innovatint - order([orderID])',
  },
  onSave: console.log,
  menuItems: [],
};

const MailEditor = ({
  initialValue,
  onSave,
  onCancel,
  menuItems,
  variables,
  translate,
}: Props): JSX.Element => {
  const editorRef = useRef<any>(null);
  const joditRef = useRef<any>(null);

  const config = {
    // Capture the underlying Jodit instance when it’s initialized
    events: {
      afterInit: (j: any) => {
        joditRef.current = j;
      },
    },
    height: 400,
    readonly: false,
    toolbarSticky: true,
    // Optional quality-of-life:
    saveSelectionOnBlur: true,
    // readonly: false,
    removeButtons: ['preview', 'print', 'speechRecognize'],
  };

  const { t } = useTranslation();
  const trans = translate || t;

  const [name, setName] = useState('');
  const [size, setSize] = useState('A4');
  const [subject, setSubject] = useState('Innovatint - order([orderID])');
  const [toggle, setToggle] = useState(false);
  const [addBarcodeVisible, setAddBarcodeVisible] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [barcode_types, setBarcodeTypes] = useState(['qrcode']);
  const [content, setContent] = useState(initialValue.template);
  const [itemVariables, setItemVariables] = useState<VariableType[]>([]);

  useEffect(() => {
    setName(initialValue.name);
    setSize(initialValue.pageSize);
    setSubject(initialValue.subject);
    setContent(initialValue.template);
  }, [
    initialValue.name,
    initialValue.pageSize,
    initialValue.subject,
    initialValue.template,
  ]);

  const saveTemplate = () => {
    onSave({
      template: content,
      name: name,
      subject: subject || 'Innovatint - order([orderID])',
      pageSize: size,
    });
  };

  const toggleModal = () => {
    setToggle(!toggle);
  };

  const insertVariable = useCallback((html: string) => {
    const j =
      joditRef.current ??
      // v3 path (commonly)
      (editorRef.current as any)?.editor ??
      // v4 path (some builds)
      (editorRef.current as any)?.jodit;

    if (!j) {
      console.warn('Jodit not ready yet'); // avoid calling focus() on undefined
      return;
    }

    const editor = j.editor;
    if (editor && editor.lastChild) {
      j.selection.setCursorIn(editor.lastChild, false);
    } else {
      j.selection.setCursorIn(editor, false);
    }

    j.selection.insertHTML(`[${html}]`);
  }, []);

  return (
    <div className={'em-background-div'}>
      <div className={'em-flex-div'}>
        <p className={'em-paragraph'}>{trans('lbl.name.colon', 'Name:')}</p>
        <input
          className={'em-input-field'}
          value={name}
          data-testid="TemplateName"
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className={'em-flex-div'}>
        <p className={'em-paragraph'}>
          {trans('lbl.subject.colon', 'Subject:')}
        </p>
        <input
          className={'em-input-field'}
          value={subject}
          data-testid="Subject"
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div className={'em-flex-div'}>
        <p className={'em-paragraph'}>
          {trans('lbl.insert_variable.colon', 'Insert variable:')}
        </p>
        <select
          data-cy={'variable_grp'}
          className={'em-select'}
          onChange={(e) => {
            const item = menuItems.find((v) => v.value === e.target.value);
            setItemVariables(item?.variables || []);
          }}
        >
          <option value="">Select variable group...</option>
          {menuItems.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>

        <select
          data-cy={'variable'}
          style={{ marginLeft: '2rem' }}
          className={'em-select'}
          onChange={(e) => {
            if (e.target.value) insertVariable(e.target.value);
            e.target.selectedIndex = 0;
          }}
        >
          <option value="">Insert variable...</option>
          {itemVariables.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
        <button className={'em-save-button'} onClick={toggleModal}>
          {trans('fn.preview', 'Preview')}
        </button>
      </div>
      <JoditEditor
        value={content}
        ref={editorRef}
        config={config}
        onBlur={(value) => setContent(value)}
      />
      <div className={'em-flex-div'}>
        <button
          className={'em-cancel-button'}
          onClick={onCancel}
          data-testid="cancel"
        >
          {trans('fn.cancel', 'Cancel')}
        </button>
        <button
          onClick={saveTemplate}
          disabled={!name || !subject}
          data-testid="save"
          className={'em-save-button'}
        >
          {trans('fn.save', 'Save')}
        </button>
      </div>
      <PreviewModal
        template={content}
        ei_values={variables}
        toggleModal={toggleModal}
        isOpen={toggle}
        translate={trans}
      />
      <AddBarcodeModal
        barcode_types={barcode_types}
        data_key={barcode}
        toggleModal={() => setAddBarcodeVisible(false)}
        isOpen={addBarcodeVisible}
        editorRef={editorRef}
      />
    </div>
  );
};

MailEditor.defaultProps = defaultProps;

export default MailEditor;
